import {
  ColDef,
  ExcelDataType,
  ExcelStyle,
  ValueFormatterParams,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import { Tooltip } from 'primereact/tooltip';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  portfolioNameAtom,
  selectedTickerFilterAtom,
  aiReasoningMapAtom,
  agentChatInjectionAtom,
  agentChatVisibleAtom,
  agentChatPrefillAtom,
  agentChatReasoningContextAtom,
} from '~/atoms/dashboardAtom';
import { createAgTheme } from '~/components/ui/tables/ag/agTheme';
import { siteThemeMode } from '~/atoms/userPreferencesAtoms';
import { openPopoutWindow } from '~/utils/popoutUtils';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { WidgetHeader } from '../../common/WidgetHeader';

export interface SectorAllocationRow {
  // Hierarchy path for tree structure
  orgHierarchy: string[];

  // Sector level data (for parent rows)
  sector?: string;
  sector_total?: number;

  // Holding level data (for child rows)
  ticker?: string;
  full_ticker?: string;
  isin?: string;
  company_name?: string;
  current_holdings?: number | null;
  proposed_holdings?: number | null;

  // Derived delta value for display/aggregation
  delta?: number | null;

  // Current holdings metrics
  current_beta?: number | null;
  current_var_95_1d_impact?: number | null;
  current_cvar_95_1d_impact?: number | null;
  current_ytd_return?: number | null;

  // Proposed holdings metrics
  proposed_beta?: number | null;
  proposed_var_95_1d_impact?: number | null;
  proposed_cvar_95_1d_impact?: number | null;
  proposed_ytd_return?: number | null;
}

export type SectorAllocationWidgetProps = {
  data: SectorAllocationRow[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  snapshotId?: string;
  currency?: string;
};

export const SectorAllocationWidget: React.FC<SectorAllocationWidgetProps> = ({
  data,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'Allocation Analysis',
  snapshotId,
  currency = 'EUR',
}) => {
  const gridRef = useRef<AgGridReact<SectorAllocationRow>>(null);
  const themeMode = useAtomValue(siteThemeMode);
  const gridTheme = useMemo(() => createAgTheme(themeMode), [themeMode]);
  const setSelectedTickerFilter = useSetAtom(selectedTickerFilterAtom);
  const aiReasoningMap = useAtomValue(aiReasoningMapAtom);
  const setAgentChatInjection = useSetAtom(agentChatInjectionAtom);
  const setAgentChatVisible = useSetAtom(agentChatVisibleAtom);
  const setAgentChatPrefill = useSetAtom(agentChatPrefillAtom);
  const setReasoningContext = useSetAtom(agentChatReasoningContextAtom);
  const portfolioName = useAtomValue(portfolioNameAtom);
  const pendingTickerRef = useRef<{
    ticker: string;
    displayName?: string | null;
    candidateKeys: string[];
  } | null>(null);

  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  // Add popout functionality using route-based approach
  const handlePopout = useCallback(() => {
    openPopoutWindow({
      widgetType: 'sector-allocation',
      snapshotId: snapshotId || undefined,
      title: title || 'Allocation Analysis',
    });
  }, [snapshotId, title]);

  const handlePopoutWrapper = useCallback(
    (_widgetId: string) => {
      handlePopout();
    },
    [handlePopout],
  );

  // Shared style for truncating cell content with ellipsis
  const cellStyle = useMemo(
    () => ({
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
      minWidth: 0,
    }),
    [],
  );

  const numericCellStyle = useMemo(
    () => ({
      ...cellStyle,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      textAlign: 'right' as const,
    }),
    [cellStyle],
  );

  const centerCellStyle = useMemo(
    () => ({
      ...cellStyle,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center' as const,
    }),
    [cellStyle],
  );

  // Format percentage values
  const percentageFormatter = (params: { value: number | null }) => {
    if (params.value === null || params.value === undefined) {
      return '0.00%';
    }
    return `${params.value.toFixed(2)}%`;
  };

  // Format delta values with +/- sign
  const deltaFormatter = (
    params: ValueFormatterParams<SectorAllocationRow>,
  ) => {
    const current = params.data?.current_holdings ?? 0;
    const proposed = params.data?.proposed_holdings ?? 0;

    const delta = proposed - current;
    const sign = delta > 0 ? '+' : '';
    return `${sign}${delta.toFixed(2)}%`;
  };

  // Format currency impact values (VaR/CVaR) with compact notation for large numbers
  const currencyImpactFormatter = (params: {
    value: number | null | undefined;
  }) => {
    if (
      params.value === null ||
      params.value === undefined ||
      typeof params.value !== 'number'
    ) {
      return '-';
    }
    const absValue = Math.abs(params.value);
    // Use compact notation for values >= 10,000
    if (absValue >= 10000) {
      return new Intl.NumberFormat('en-GB', {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(params.value);
    }
    // For smaller values, show as whole number with commas
    return new Intl.NumberFormat('en-GB', {
      maximumFractionDigits: 0,
    }).format(params.value);
  };

  // Watch for aiReasoningMap updates when we're waiting for a ticker
  // This handles the case where the AI Recommendations widget is on the page and still loading
  useEffect(() => {
    if (!pendingTickerRef.current) return;

    const { ticker, displayName, candidateKeys } = pendingTickerRef.current;
    const reasoningEntry = candidateKeys.reduce<
      (typeof aiReasoningMap)[string] | undefined
    >((found, key) => found || aiReasoningMap[key], undefined);

    if (reasoningEntry) {
      // Found reasoning, inject it
      const reasoningContent =
        reasoningEntry?.aiReasoning || reasoningEntry?.reason;

      if (reasoningContent) {
        setAgentChatVisible(true);
        const companyLabel =
          displayName || reasoningEntry?.companyName || ticker;
        const message = `AI reasoning for ${companyLabel} (${ticker}):\n\n${reasoningContent}`;
        setAgentChatInjection({
          id: Date.now(),
          ticker,
          content: message,
        });
        // Store reasoning context for follow-up messages
        setReasoningContext({
          ticker,
          reasoning: reasoningContent,
        });
      } else {
        setAgentChatVisible(true);
        const fallbackLabel =
          displayName || reasoningEntry?.companyName || ticker;
        const fallbackQuestion = `Can you explain the allocation rationale for ${fallbackLabel}?`;
        setAgentChatPrefill(fallbackQuestion);
        // Clear reasoning context if no reasoning content
        setReasoningContext(null);
      }
      pendingTickerRef.current = null;
    }
  }, [
    aiReasoningMap,
    setAgentChatInjection,
    setAgentChatVisible,
    setAgentChatPrefill,
    setReasoningContext,
  ]);

  const handleTickerSelection = useCallback(
    (rawTicker: string, displayName?: string | null) => {
      if (!rawTicker) {
        return;
      }

      setSelectedTickerFilter(rawTicker);
      const normalizedTicker = rawTicker.toUpperCase();
      const candidateKeys = Array.from(
        new Set(
          [
            normalizedTicker,
            normalizedTicker.split(':')[0],
            normalizedTicker.split('.')[0],
            normalizedTicker.split(' ')[0],
            normalizedTicker.replace(/[^A-Z0-9]/g, ''),
          ].filter(Boolean),
        ),
      );

      const reasoningEntry = candidateKeys.reduce<
        (typeof aiReasoningMap)[string] | undefined
      >((found, key) => found || aiReasoningMap[key], undefined);

      if (!reasoningEntry) {
        // No reasoning found - store pending ticker info in case data becomes available
        // (e.g., if AI Recommendations widget is on the page and still loading)
        pendingTickerRef.current = {
          ticker: normalizedTicker,
          displayName,
          candidateKeys,
        };

        // Immediately open chat with question
        // If reasoning becomes available later, the useEffect will handle it
        setAgentChatVisible(true);
        const question = `Can you explain the allocation rationale for ${displayName || normalizedTicker}?`;
        setAgentChatPrefill(question);
        // Clear reasoning context when no reasoning found
        setReasoningContext(null);
        return;
      }

      const reasoningContent =
        reasoningEntry?.aiReasoning || reasoningEntry?.reason;

      if (reasoningContent) {
        setAgentChatVisible(true);
        const companyLabel =
          displayName || reasoningEntry?.companyName || normalizedTicker;
        const message = `AI reasoning for ${companyLabel} (${normalizedTicker}):\n\n${reasoningContent}`;
        setAgentChatInjection({
          id: Date.now(),
          ticker: normalizedTicker,
          content: message,
        });
        // Store reasoning context for follow-up messages
        setReasoningContext({
          ticker: normalizedTicker,
          reasoning: reasoningContent,
        });
      } else {
        setAgentChatVisible(true);
        const fallbackLabel =
          displayName || reasoningEntry?.companyName || normalizedTicker;
        const fallbackQuestion = `Can you explain the allocation rationale for ${fallbackLabel}?`;
        setAgentChatPrefill(fallbackQuestion);
        // Clear reasoning context if no reasoning content
        setReasoningContext(null);
      }
    },
    [
      aiReasoningMap,
      setAgentChatInjection,
      setAgentChatVisible,
      setAgentChatPrefill,
      setSelectedTickerFilter,
      setReasoningContext,
    ],
  );

  const CompanyNameCellRenderer = useCallback(
    (props: { value: string; data: SectorAllocationRow }) => {
      // If it's a sector row, just show the sector name
      if (props.data?.sector) {
        return (
          <span style={{ fontWeight: '600', fontSize: '1rem' }}>
            {props.data.sector}
          </span>
        );
      }

      // For holdings, show clickable company name
      const displayName = props.data?.company_name || props.value;
      const ticker =
        props.data?.ticker ||
        props.data?.full_ticker ||
        props.value ||
        props.data?.isin ||
        '';

      return (
        <span
          className="ticker-cell-clickable ticker-tooltip-target"
          onClick={() => handleTickerSelection(ticker, displayName)}
          data-pr-tooltip={ticker}
          style={{
            color: 'var(--bq-color-info-strong)',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          {displayName}
        </span>
      );
    },
    [handleTickerSelection],
  );

  // Style delta cells with color
  const deltaStyler = (params: { data: SectorAllocationRow }) => {
    const current = params.data.current_holdings ?? 0;
    const proposed = params.data.proposed_holdings ?? 0;

    const delta = proposed - current;

    if (delta > 0) {
      return { color: 'var(--bq-color-positive)', fontWeight: '600' };
    } else if (delta < 0) {
      return { color: 'var(--bq-color-negative)', fontWeight: '600' };
    }
    return { color: 'var(--bq-color-neutral)', fontWeight: '600' };
  };

  // AG Grid column definitions with tree structure
  // Version key to force re-render when columns change: v2
  const columnDefs = useMemo<ColDef<SectorAllocationRow>[]>(
    () => [
      {
        field: 'ticker',
        headerName: 'Company',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 220,
        flex: 1,
        headerClass: 'ag-left-aligned-header',
        cellRenderer: CompanyNameCellRenderer,
        valueGetter: (params) => {
          // For sector rows, show sector name
          if (params.data?.sector) {
            return params.data.sector;
          }
          // For holdings, show company name or ticker
          return (
            params.data?.company_name ||
            params.data?.ticker ||
            params.data?.full_ticker ||
            ''
          );
        },
        cellStyle: (params) => ({
          ...cellStyle,
          display: 'flex',
          alignItems: 'center',
          paddingLeft: params.node?.level === 0 ? '8px' : '16px',
        }),
        tooltipValueGetter: (params) =>
          params.data?.company_name ||
          params.data?.ticker ||
          params.data?.full_ticker ||
          '',
      },
      {
        field: 'isin',
        headerName: 'ISIN',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 150,
        flex: 1,
        headerClass: 'ag-center-aligned-header',
        valueGetter: (params) => {
          // Only show ISIN for holdings, not sectors
          return params.data?.isin || '';
        },
        cellStyle: centerCellStyle,
        tooltipValueGetter: (params) => params.data?.isin || '',
      },
      {
        field: 'current_holdings',
        headerName: `${portfolioName || 'Current Holdings'} (%)`,
        sortable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 180,
        flex: 1,
        valueFormatter: percentageFormatter,
        type: 'numericColumn',
        headerClass: 'ag-left-aligned-header',
        cellClass: 'percentageCell',
        aggFunc: 'sum', // Aggregate holdings for sector totals
        cellStyle: numericCellStyle,
      },
      {
        field: 'proposed_holdings',
        headerName: 'AI Portfolio (%)',
        sortable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 180,
        flex: 1,
        valueFormatter: percentageFormatter,
        type: 'numericColumn',
        headerClass: 'ag-left-aligned-header',
        cellClass: 'percentageCell',
        aggFunc: 'sum', // Aggregate holdings for sector totals
        cellStyle: numericCellStyle,
      },
      {
        field: 'delta',
        headerName: 'Delta (%)',
        sortable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 160,
        flex: 1,
        sort: 'desc',
        valueGetter: (params) => {
          const current = params.data?.current_holdings ?? 0;
          const proposed = params.data?.proposed_holdings ?? 0;
          return proposed - current;
        },
        valueFormatter: deltaFormatter,
        cellStyle: (params) => ({
          ...numericCellStyle,
          ...deltaStyler({ data: params.data as SectorAllocationRow }),
        }),
        type: 'numericColumn',
        headerClass: 'ag-left-aligned-header',
        cellClass: 'percentageCell',
        aggFunc: 'sum', // Aggregate delta for sector totals
      },
      {
        field: 'current_beta',
        headerName: `${portfolioName || 'Current'} Beta`,
        sortable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 140,
        flex: 1,
        valueFormatter: (params) => {
          if (
            params.value === null ||
            params.value === undefined ||
            typeof params.value !== 'number'
          ) {
            return '-';
          }
          return params.value.toFixed(2);
        },
        type: 'numericColumn',
        headerClass: 'ag-left-aligned-header',
        cellStyle: numericCellStyle,
      },
      {
        field: 'proposed_beta',
        headerName: 'AI Beta',
        sortable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 140,
        flex: 1,
        valueFormatter: (params) => {
          if (
            params.value === null ||
            params.value === undefined ||
            typeof params.value !== 'number'
          ) {
            return '-';
          }
          return params.value.toFixed(2);
        },
        type: 'numericColumn',
        headerClass: 'ag-left-aligned-header',
        cellStyle: numericCellStyle,
      },
      {
        field: 'current_var_95_1d_impact',
        headerName: `${portfolioName || 'Current'} VaR 1d (${currency})`,
        sortable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 180,
        flex: 1,
        valueFormatter: currencyImpactFormatter,
        type: 'numericColumn',
        headerClass: 'ag-left-aligned-header',
        cellStyle: numericCellStyle,
      },
      {
        field: 'proposed_var_95_1d_impact',
        headerName: `AI VaR 1d (${currency})`,
        sortable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 160,
        flex: 1,
        valueFormatter: currencyImpactFormatter,
        type: 'numericColumn',
        headerClass: 'ag-left-aligned-header',
        cellStyle: numericCellStyle,
      },
      {
        field: 'current_cvar_95_1d_impact',
        headerName: `${portfolioName || 'Current'} CVaR 1d (${currency})`,
        sortable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 190,
        flex: 1,
        valueFormatter: currencyImpactFormatter,
        type: 'numericColumn',
        headerClass: 'ag-left-aligned-header',
        cellStyle: numericCellStyle,
      },
      {
        field: 'proposed_cvar_95_1d_impact',
        headerName: `AI CVaR 1d (${currency})`,
        sortable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 170,
        flex: 1,
        valueFormatter: currencyImpactFormatter,
        type: 'numericColumn',
        headerClass: 'ag-left-aligned-header',
        cellStyle: numericCellStyle,
      },
      {
        field: 'current_ytd_return',
        headerName: `${portfolioName || 'Current'} YTD Return (%)`,
        sortable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 200,
        flex: 1,
        valueFormatter: (params) => {
          if (
            params.value === null ||
            params.value === undefined ||
            typeof params.value !== 'number'
          ) {
            return '-';
          }
          return `${params.value.toFixed(2)}%`;
        },
        type: 'numericColumn',
        headerClass: 'ag-left-aligned-header',
        cellClass: 'percentageCell',
        cellStyle: numericCellStyle,
      },
      {
        field: 'proposed_ytd_return',
        headerName: 'AI YTD Return (%)',
        sortable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 180,
        flex: 1,
        valueFormatter: (params) => {
          if (
            params.value === null ||
            params.value === undefined ||
            typeof params.value !== 'number'
          ) {
            return '-';
          }
          return `${params.value.toFixed(2)}%`;
        },
        type: 'numericColumn',
        headerClass: 'ag-left-aligned-header',
        cellClass: 'percentageCell',
        cellStyle: numericCellStyle,
      },
    ],
    [
      portfolioName,
      cellStyle,
      numericCellStyle,
      centerCellStyle,
      CompanyNameCellRenderer,
      currency,
    ],
  );

  // Auto group column definition for tree structure
  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      headerName: 'Sector / Company',
      minWidth: 300,
      cellRendererParams: {
        suppressCount: true, // Don't show count of children
      },
      flex: 1,
      cellStyle: cellStyle,
      tooltipValueGetter: (params) =>
        Array.isArray(params.value)
          ? params.value.join(' / ')
          : params.value || '',
    }),
    [cellStyle],
  );

  // Default column properties
  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      enableCellChangeFlash: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    }),
    [],
  );

  // Excel export styles
  const excelStyles = useMemo<ExcelStyle[]>(
    () => [
      {
        id: 'percentageCell',
        dataType: 'number' as ExcelDataType,
        numberFormat: { format: '0.00%' },
      },
    ],
    [],
  );

  // Get data path for tree structure
  const getDataPath = useCallback((data: SectorAllocationRow) => {
    return data.orgHierarchy;
  }, []);

  // Handle grid resize when container size changes
  useEffect(() => {
    const handleResize = () => {
      if (gridRef.current?.api) {
        gridRef.current.api.sizeColumnsToFit();
      }
    };

    const widgetElement = document.getElementById(widgetId);
    if (widgetElement) {
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(widgetElement);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [widgetId]);

  const handleExportCSV = useCallback(() => {
    gridRef.current?.api.exportDataAsExcel({
      fileName: `sector_allocations_${snapshotId || 'export'}.xlsx`,
      processCellCallback: (params) => {
        const colId = params.column.getColId();

        // For percentage columns, convert to decimal
        if (colId === 'current_holdings' || colId === 'proposed_holdings') {
          const rawValue =
            params.node?.data?.[colId as keyof SectorAllocationRow];
          if (rawValue === null || rawValue === undefined) {
            return 0;
          }
          return (rawValue as number) / 100;
        }

        // For delta column, calculate from raw data
        if (colId === 'delta') {
          const current = params.node?.data?.current_holdings ?? 0;
          const proposed = params.node?.data?.proposed_holdings ?? 0;
          const delta = proposed - current;
          return delta / 100;
        }

        return params.value;
      },
    });
  }, [snapshotId]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget || undefined}
          refetch={() => refetch()}
          onPopout={handlePopoutWrapper}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget || undefined}
          onPopout={handlePopoutWrapper}
          widgetId={widgetId}
          refetch={() => refetch()}
        />
        <NoDataAvailableTemplate
          icon="pi pi-inbox"
          title="No snapshot selected"
          subTitle="Please select a snapshot to view Allocation Analysis"
        />
      </div>
    );
  }

  return (
    <div className="hover-container h-full flex flex-column" id={widgetId}>
      <Tooltip target=".ticker-tooltip-target" position="top" />
      <WidgetHeader
        title={title}
        removeWidget={removeWidget || undefined}
        onPopout={handlePopoutWrapper}
        widgetId={widgetId}
        refetch={() => refetch()}
        children={
          <div className="flex gap-2 ml-2">
            {data.length > 0 && (
              <Button
                size="small"
                icon="pi pi-download"
                onClick={handleExportCSV}
                tooltip="Export to XLSX"
                tooltipOptions={{ position: 'bottom' }}
                style={{
                  padding: '0.4375rem 0.65625rem',
                }}
                pt={{
                  root: {
                    style: {
                      color: 'var(--bq-button-text, #ffffff)',
                    },
                  },
                  icon: {
                    style: {
                      color: 'var(--bq-button-icon, #ffffff) !important',
                    },
                  },
                }}
              />
            )}
          </div>
        }
      />

      <div className="flex-1 overflow-hidden p-3">
        <Card
          className="h-full"
          pt={{
            root: {
              className: 'h-full',
              style: {
                borderRadius: '0.5rem',
                overflow: 'hidden',
              },
            },
            body: {
              className: 'h-full p-0',
              style: {
                borderRadius: '0.5rem',
                overflow: 'hidden',
              },
            },
            content: {
              className: 'h-full p-0',
            },
          }}
        >
          <div style={{ height: '100%', width: '100%' }}>
            {data.length === 0 ? (
              <div className="flex align-items-center justify-content-center h-full">
                <div
                  className="text-center"
                  style={{ color: 'var(--bq-text-secondary)' }}
                >
                  <i className="pi pi-inbox text-4xl mb-3 block"></i>
                  <p
                    className="text-xl"
                    style={{ color: 'var(--bq-text-primary)' }}
                  >
                    No Allocation Analysis data available
                  </p>
                  <p
                    className="text-sm mt-2"
                    style={{ color: 'var(--bq-text-tertiary)' }}
                  >
                    Data will appear here when available
                  </p>
                </div>
              </div>
            ) : (
              <AgGridReact<SectorAllocationRow>
                ref={gridRef}
                rowData={data}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                autoGroupColumnDef={autoGroupColumnDef}
                treeData={true}
                getDataPath={getDataPath}
                groupDefaultExpanded={1} // Expand Total row by default, sectors collapsed
                excelStyles={excelStyles}
                theme={gridTheme}
                loadThemeGoogleFonts={true}
                suppressCellFocus={false}
                enableCellTextSelection={true}
                ensureDomOrder={true}
                onGridReady={() => {
                  gridRef.current?.api.sizeColumnsToFit();
                }}
                autoSizeStrategy={{
                  type: 'fitGridWidth',
                  defaultMinWidth: 100,
                }}
                enableRangeSelection={true}
                defaultCsvExportParams={{
                  fileName: `sector_allocations_${snapshotId || 'export'}.csv`,
                }}
              />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
